import Flex from 'UIKit/Box/Flex'
import Button from 'UIKit/Button'
import Text from 'UIKit/Text'
import ConfirmationModal from 'components/ConfirmationModal'
import { BoosterWagerBonus, LevelUpBonus, UserBonus, WageringConditionBonus } from 'config/types/bonus/userBonus'

import CancelBonusSurveyModal from 'components/Survey/CancelBonusSurveyModal'
import { BonusStatusEnums } from 'config/types/bonus'
import useModal from 'hooks/useModal'
import { useRequest } from 'hooks/useRequest'
import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import BonusService from 'services/BonusService'
import { useUserBonus } from 'state/bonus/hooks/useUserBonus'
import styled from 'styled-components'
import { colors } from 'theme/colors'
import { delayed } from 'utils'
import { forkjoinRequest } from 'utils/requestHelper'
import { HunnyToast } from 'utils/toastify'
import { ValidatedBonusStatusEnums, useValidateBonus } from 'views/BonusCenter/hooks'
import BonusAction from '../BonusAction'
import BonusInvalid from '../BonusInvalid'
import BonusItemCountdown from '../BonusItem/BonusItemCountdown'
import BonusCancelConfirmationModalContent from './BonusCancelConfirmationModalContent'

interface BonusDetailsActionProps {
  bonus: UserBonus
  setBonusInstance: (bonus: any) => void
  onBonusUpdate?: (bonus: UserBonus) => void
  onDismiss: () => void
}

const BonusDetailsAction: React.FC<BonusDetailsActionProps> = ({
  onBonusUpdate,
  setBonusInstance,
  onDismiss,
  bonus,
}) => {
  const { t } = useTranslation()
  const { fetchBonuses } = useUserBonus()
  const validatedResult: ValidatedBonusStatusEnums = useValidateBonus(bonus)
  const { execute } = useRequest()
  const [handlePresentCancelConfirmationModal] = useModal(ConfirmationModal)
  const [onPresentSurveyBonus] = useModal(CancelBonusSurveyModal)

  const handleBonusUsed = useCallback(async (useSuccessfully: boolean) => {
    if (bonus instanceof UserBonus) {
      if (!useSuccessfully) {
        const newBonus = await execute(BonusService.getUserBonusDetails(bonus.id))
        if (newBonus.data) {
          setBonusInstance({ instance: newBonus.data })
          onBonusUpdate(newBonus.data)
        }
      } else {
        await delayed(200)
        onDismiss()
      }
    }
  }, [])

  const handleCancelBonus = useCallback(async () => {
    HunnyToast.show(t('Your bonus is canceling'), '', { autoClose: 1500 })
    const [isSuccessful] = await forkjoinRequest([BonusService.cancel(bonus.id), delayed(1500)])
    if (isSuccessful) {
      await fetchBonuses()
      onPresentSurveyBonus({
        bonusId: bonus.id.toString(),
      })
      HunnyToast.success(t('Successful!'), t('Your bonus has been cancelled successfully.'))
    } else {
      const newBonus = await execute(BonusService.getUserBonusDetails(bonus.id))
      if (newBonus.data) {
        setBonusInstance({ instance: newBonus.data })
        onBonusUpdate(newBonus.data)
      }
    }

    onDismiss()
  }, [])

  return (
    <>
      {bonus instanceof LevelUpBonus && bonus.status == BonusStatusEnums.Active ? null : bonus instanceof
          BoosterWagerBonus && bonus.status === BonusStatusEnums.Active ? (
        <Flex justifyContent="center" px="40px" width="100%">
          {!bonus.condition.isExpired && <BonusItemCountdown expiryDate={bonus.condition.expiryDate} />}
        </Flex>
      ) : (
        <Flex justifyContent="center" width="100%">
          {validatedResult === ValidatedBonusStatusEnums.Available ? (
            <Flex width="100%" flexDirection="column" justifyContent="center" alignItems="center">
              <StyledBonusAction
                width="100%"
                trackingModule="bonusDetailsModal"
                bonus={bonus}
                onBonusSubmitted={handleBonusUsed}
                $active={validatedResult === ValidatedBonusStatusEnums.Available}
              />
              {bonus instanceof WageringConditionBonus &&
                bonus.status === BonusStatusEnums.Active &&
                !bonus.condition.isExpired && (
                  <Button
                    className="bonusDetailsModal-cancelBonusBtn"
                    variant="text"
                    marginX="auto"
                    width="100%"
                    mt="4px"
                    onClick={() => {
                      handlePresentCancelConfirmationModal({
                        onAccept: handleCancelBonus,
                        onCancel: () => {},
                        acceptButtonBackground: colors.error,
                        content: <BonusCancelConfirmationModalContent bonus={bonus as WageringConditionBonus} />,
                        acceptButtonContent: t('Yes, Cancel'),
                        cancelButtonContent: t('No, Keep the Bonus'),
                        title: t('Cancel Bonus'),
                      })
                    }}
                  >
                    <Text small color="error">
                      <Trans>Cancel bonus</Trans>
                    </Text>
                  </Button>
                )}
            </Flex>
          ) : (
            <StyledBonusInvalid bonus={bonus} />
          )}
        </Flex>
      )}
    </>
  )
}

const StyledBonusInvalid = styled(BonusInvalid)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background: ${({ theme: { colors } }) => colors.backgroundAlt5};
  border-radius: ${({ theme: { radii } }) => radii.tiny};
  line-height: normal !important;
`

const StyledBonusAction = styled(BonusAction)<{ $active: boolean }>`
  height: 48px;
  width: 100%;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    background: #1d2533 !important;
  }
`

export default BonusDetailsAction
